import { BarLayer, ResponsiveBar } from "@nivo/bar";
import { hexColorRegex } from "validations/matchers";
import { BarsChartData } from "./BarsChartInterfaces";
import { HorizontalBarValue } from "components/HorizontalBarValue/HorizontalBarValue.view";
import { VerticalBarValue } from "components/VerticalBarValue/VerticalBarValue.view";
import { truncateLongText } from "utilities/utilities";
import { Typography, useTheme } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { useStyles } from "./BarsChart.styles";

export const BarsChart = ({ data, index, orientation }: BarsChartData) => {
    const classes = useStyles();
    const keys: string[] = [];
    const colors: string[] = [];
    const themeStyles = useTheme();

    data.forEach((dataItem) => {
        Object.values(dataItem).forEach((value) => {
            if (typeof value === "string" && hexColorRegex.test(value) && !colors.includes(value)) {
                colors.push(value);
            }
        });
    });
    data.forEach((dataItem) => {
        Object.keys(dataItem).forEach((key) => {
            if (key !== index && !keys.includes(key) && !key.includes("Color")) {
                keys.push(key);
            }
        });
    });

    const theme = {
        axis: {
            ticks: {
                text: {
                    fontSize: "13px",
                    fontFamily: "Source Sans 3",
                    fill: themeStyles.palette.text.secondary,
                },
            },
        },
        tooltip: {
            container: {
                display: "none",
            },
        },
    };

    const barsOrientation = orientation === "vertical" ? VerticalBarValue : HorizontalBarValue;
    const BarLayers = barsOrientation as unknown as BarLayer<{ [key: string]: string | number }>;

    return (
        <ResponsiveBar
            data={data}
            keys={keys}
            theme={theme}
            indexBy={index}
            layout={orientation}
            margin={{
                top: orientation === "horizontal" ? 20 : 30,
                right: orientation === "horizontal" ? 100 : 40,
                bottom: orientation === "horizontal" ? 20 : 100,
                left: orientation === "horizontal" ? 150 : 40,
            }}
            padding={0.4}
            enableGridY={orientation === "horizontal" ? false : true}
            enableGridX={orientation === "horizontal" ? true : false}
            valueScale={{ type: "linear" }}
            indexScale={{ type: "band", round: true }}
            colors={colors}
            innerPadding={1}
            enableLabel={false}
            axisTop={null}
            axisBottom={
                orientation === "horizontal"
                    ? null
                    : {
                          tickSize: 0,
                          tickPadding: 10,
                          renderTick(props) {
                              const { x, y, value } = props;
                              return (
                                  <Tooltip
                                      title={<Typography className={classes.tooltipTexts}>{value}</Typography>}
                                      placement="bottom"
                                      PopperProps={{
                                          className: classes.tooltip,
                                      }}
                                  >
                                      <g transform={`translate(${x} ${y}) rotate(-45)`}>
                                          <text
                                              x={0}
                                              y={10}
                                              textAnchor="end"
                                              dominantBaseline="central"
                                              fill={themeStyles.palette.text.secondary}
                                              className={classes.text}
                                          >
                                              <>{truncateLongText(value, 15)}</>
                                          </text>
                                      </g>
                                  </Tooltip>
                              );
                          },
                      }
            }
            axisLeft={
                orientation === "horizontal"
                    ? {
                          tickSize: 0,
                          tickPadding: 5,
                          format: (value) => {
                              return truncateLongText(value, 20);
                          },
                          renderTick(props) {
                              const { x, y, value } = props;
                              return (
                                  <Tooltip
                                      title={<Typography className={classes.tooltipTexts}>{value}</Typography>}
                                      placement="left"
                                      PopperProps={{
                                          className: classes.tooltip,
                                      }}
                                  >
                                      <g transform={`translate(${x},${y})`}>
                                          <text
                                              x={-2}
                                              y={0}
                                              textAnchor="end"
                                              dominantBaseline="central"
                                              fill={themeStyles.palette.text.secondary}
                                              className={classes.text}
                                          >
                                              <>{truncateLongText(value, 20)}</>
                                          </text>
                                      </g>
                                  </Tooltip>
                              );
                          },
                      }
                    : null
            }
            axisRight={null}
            labelSkipWidth={12}
            labelSkipHeight={12}
            labelTextColor={{
                from: "color",
                modifiers: [["darker", 1.6]],
            }}
            legends={[]}
            role="application"
            layers={["grid", "axes", "bars", BarLayers, "markers", "legends"]}
        />
    );
};
